// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
import {
  averageValues,
} from "../core/math.js";
import {
  clampScore,
  elapsedAnchoredSampleForAnalysis,
  median,
} from "./utils.js";
import {
  powerChangeContextParameters,
} from "./detectors/power-change-context.js";

function round(value, digits = 1) {
  if (!Number.isFinite(value)) return null;
  const scale = 10 ** digits;
  return Math.round(value * scale) / scale;
}

function cylinderKeys(prefix, maxCylinder = 16) {
  return Array.from({ length: maxCylinder }, (_, index) => `${prefix}${index + 1}`);
}

function cylinderNumberFromKey(key) {
  const match = String(key ?? "").match(/^(?:cht|egt)(\d+)$/i);
  return match ? Number(match[1]) : null;
}

function channelAvailable(points, key) {
  return points.some((point) => Number.isFinite(point?.[key]));
}

function sampleForAnalysis(data, targetCount = powerChangeContextParameters.maxAnalysisSamples) {
  return elapsedAnchoredSampleForAnalysis(data, targetCount);
}

function sortedTimedPoints(data) {
  return sampleForAnalysis(data)
    .filter((point, index) => Number.isFinite(point?.elapsedSeconds ?? index))
    .map((point, index) => ({
      ...point,
      elapsedSeconds: Number.isFinite(point.elapsedSeconds) ? point.elapsedSeconds : index,
    }))
    .sort((a, b) => a.elapsedSeconds - b.elapsedSeconds);
}

function segmentValues(points, valueForPoint, start = true) {
  const values = points.map(valueForPoint).filter(Number.isFinite);
  if (!values.length) return [];
  const segmentSize = Math.max(1, Math.floor(values.length / 3));
  return start ? values.slice(0, segmentSize) : values.slice(-segmentSize);
}

function beforeAfter(points, valueForPoint) {
  const before = median(segmentValues(points, valueForPoint, true));
  const after = median(segmentValues(points, valueForPoint, false));
  return {
    before,
    after,
    delta: Number.isFinite(before) && Number.isFinite(after) ? after - before : null,
  };
}

function probeValuesForKeys(point, keys) {
  return keys.map((key) => point?.[key]).filter(Number.isFinite);
}

function averageProbeChange(points, keys) {
  const before = averageValues(segmentValues(points, (point) => averageValues(probeValuesForKeys(point, keys)), true));
  const after = averageValues(segmentValues(points, (point) => averageValues(probeValuesForKeys(point, keys)), false));
  return {
    before,
    after,
    delta: Number.isFinite(before) && Number.isFinite(after) ? after - before : null,
  };
}

function dominantPhase(points) {
  const counts = new Map();
  points.forEach((point) => {
    const phase = String(point?.phase ?? "").trim();
    if (!phase) return;
    counts.set(phase, (counts.get(phase) ?? 0) + 1);
  });
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
}

function presentKeys(points, prefix) {
  const keys = cylinderKeys(prefix).filter((key) => channelAvailable(points, key));
  if (keys.length) return keys;
  return channelAvailable(points, prefix) ? [prefix] : [];
}

function probeDeltas(points, keys) {
  return keys.map((key) => {
    const change = beforeAfter(points, (point) => point?.[key]);
    return {
      key,
      cylinder: cylinderNumberFromKey(key),
      before: change.before,
      after: change.after,
      delta: change.delta,
    };
  }).filter((item) => Number.isFinite(item.delta));
}

function egtCoordination(deltas, avgEgtDeltaF) {
  const presentEgtCount = deltas.length;
  if (!presentEgtCount || !Number.isFinite(avgEgtDeltaF) || avgEgtDeltaF === 0) {
    return {
      sameDirectionEgtCount: 0,
      sameDirectionEgtRatio: null,
      dominantCylinder: null,
      dominantCylinderDeltaF: null,
    };
  }
  const sign = Math.sign(avgEgtDeltaF);
  const sameDirection = deltas.filter((item) => (
    Math.sign(item.delta) === sign
    && Math.abs(item.delta) >= powerChangeContextParameters.minCylinderEgtDeltaF
  ));
  const dominant = deltas.slice().sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))[0] ?? null;
  return {
    sameDirectionEgtCount: sameDirection.length,
    sameDirectionEgtRatio: sameDirection.length / presentEgtCount,
    dominantCylinder: dominant?.cylinder ?? null,
    dominantCylinderDeltaF: dominant?.delta ?? null,
  };
}

function speedKey(points) {
  return ["ias", "tas", "groundSpeed"].find((key) => channelAvailable(points, key)) ?? null;
}

function missingContextChannels(points) {
  const missing = [];
  for (const key of ["fuelFlow", "rpm", "map"]) {
    if (!channelAvailable(points, key)) missing.push(key);
  }
  if (!speedKey(points)) missing.push("ias/tas/groundSpeed");
  if (!channelAvailable(points, "altitude")) missing.push("altitude");
  if (!channelAvailable(points, "verticalSpeed")) missing.push("verticalSpeed");
  return missing;
}

function precomputeEndIndicesByDuration(points, durations) {
  const elapsedSeconds = points.map((point) => point.elapsedSeconds);
  return durations.map((duration) => {
    const result = new Int32Array(points.length);
    let endIndex = 1;
    for (let startIndex = 0; startIndex < points.length; startIndex += 1) {
      const target = elapsedSeconds[startIndex] + duration;
      if (endIndex <= startIndex) endIndex = startIndex + 1;
      while (endIndex < points.length && elapsedSeconds[endIndex] < target) endIndex += 1;
      result[startIndex] = endIndex < points.length ? endIndex : -1;
    }
    return result;
  });
}

function allCylinderResponse(candidate) {
  if (!Number.isFinite(candidate.sameDirectionEgtRatio) || candidate.presentEgtCount < 2) return false;
  const requiredCount = Math.min(
    powerChangeContextParameters.minSameDirectionEgtCount,
    Math.ceil(candidate.presentEgtCount * powerChangeContextParameters.allCylinderEgtRatio),
  );
  return candidate.sameDirectionEgtRatio >= powerChangeContextParameters.allCylinderEgtRatio
    && candidate.sameDirectionEgtCount >= requiredCount;
}

function classifyCandidate(candidate, missingChannels) {
  const p = powerChangeContextParameters;
  const tags = [];
  const phase = String(candidate.phase ?? "");
  const missingPowerChannel = missingChannels.some((key) => ["fuelFlow", "rpm", "map"].includes(key));
  const powerAddition = [
    candidate.fuelFlowDeltaGph >= p.minFuelFlowDeltaGph,
    candidate.mapDeltaInHg >= p.minMapDeltaInHg,
    candidate.rpmDelta >= p.minRpmDelta,
    candidate.percentPowerDelta >= p.minPercentPowerDelta,
  ].some(Boolean);
  const powerReduction = [
    candidate.fuelFlowDeltaGph <= -p.minFuelFlowDeltaGph,
    candidate.mapDeltaInHg <= -p.minMapDeltaInHg,
    candidate.rpmDelta <= -p.minRpmDelta,
    candidate.percentPowerDelta <= -p.minPercentPowerDelta,
  ].some(Boolean);
  const allCylinder = allCylinderResponse(candidate);
  const singleCylinder = (candidate.sameDirectionEgtRatio ?? 0) < p.singleCylinderRatioCeiling
    && Math.abs(candidate.dominantCylinderDeltaF ?? 0) >= p.dominantCylinderDeltaF;

  if (allCylinder) tags.push("all-cylinder-egt-response");
  if (candidate.fuelFlowDeltaGph >= p.minFuelFlowDeltaGph) tags.push("fuel-flow-increase");
  if (candidate.fuelFlowDeltaGph <= -p.minFuelFlowDeltaGph) tags.push("fuel-flow-decrease");
  if (candidate.mapDeltaInHg >= p.minMapDeltaInHg) tags.push("map-increase");
  if (candidate.mapDeltaInHg <= -p.minMapDeltaInHg) tags.push("map-decrease");
  if (candidate.rpmDelta >= p.minRpmDelta) tags.push("rpm-increase");
  if (candidate.rpmDelta <= -p.minRpmDelta) tags.push("rpm-decrease");
  if (candidate.percentPowerDelta >= p.minPercentPowerDelta) tags.push("percent-power-increase");
  if (candidate.percentPowerDelta <= -p.minPercentPowerDelta) tags.push("percent-power-decrease");
  if (powerAddition) tags.push("power-addition");
  if (powerReduction) tags.push("power-reduction");
  if (/descent/i.test(phase)) tags.push("descent-context");
  if (/landing/i.test(phase)) tags.push("landing-context");
  if (/takeoff|climb/i.test(phase)) tags.push("takeoff-climb-context");
  if (/shutdown/i.test(phase)) tags.push("shutdown-context");
  if (singleCylinder) tags.push("single-cylinder-dominant");
  if (missingPowerChannel) tags.push("missing-power-channel");

  let interpretation = "not-power-change-explained";
  if (singleCylinder) {
    interpretation = "single-cylinder-event";
  } else if (/shutdown/i.test(phase) && powerReduction) {
    interpretation = "shutdown-context";
  } else if (/landing|descent/i.test(phase) && powerReduction) {
    interpretation = "low-power-landing-context";
  } else if (candidate.fuelFlowDeltaGph >= p.minFuelFlowDeltaGph && (candidate.avgEgtDeltaF ?? 0) <= -p.minAvgEgtDeltaF && allCylinder) {
    interpretation = "mixture-enrichment-context";
    tags.push("mixture-enrichment");
  } else if (candidate.fuelFlowDeltaGph <= -p.minFuelFlowDeltaGph && (candidate.avgEgtDeltaF ?? 0) >= p.mixtureLeaningEgtRiseF && allCylinder) {
    interpretation = "mixture-leaning-context";
    tags.push("mixture-leaning");
  } else if (powerAddition) {
    interpretation = "power-addition-context";
  } else if (powerReduction) {
    interpretation = "power-reduction-context";
  } else if (missingPowerChannel && allCylinder) {
    interpretation = "insufficient-context";
  }

  return { tags: [...new Set(tags)], interpretation };
}

function triggerScore(candidate) {
  const p = powerChangeContextParameters;
  return Math.max(
    Math.abs(candidate.fuelFlowDeltaGph ?? 0) / p.minFuelFlowDeltaGph,
    Math.abs(candidate.mapDeltaInHg ?? 0) / p.minMapDeltaInHg,
    Math.abs(candidate.rpmDelta ?? 0) / p.minRpmDelta,
    Math.abs(candidate.percentPowerDelta ?? 0) / p.minPercentPowerDelta,
    Math.abs(candidate.avgEgtDeltaF ?? 0) / p.minAvgEgtDeltaF,
    Math.abs(candidate.dominantCylinderDeltaF ?? 0) / p.dominantCylinderDeltaF,
  );
}

function candidateForWindow(points, rows, egtKeys, chtKeys, missingChannels) {
  if (rows.length < powerChangeContextParameters.minSamplesPerWindow) return null;
  const fuelFlow = beforeAfter(rows, (point) => point?.fuelFlow);
  const rpm = beforeAfter(rows, (point) => point?.rpm);
  const map = beforeAfter(rows, (point) => point?.map);
  const percentPower = beforeAfter(rows, (point) => point?.percentPower);
  const avgEgt = averageProbeChange(rows, egtKeys);
  const avgCht = averageProbeChange(rows, chtKeys);
  const egtDeltas = probeDeltas(rows, egtKeys);
  const coordination = egtCoordination(egtDeltas, avgEgt.delta);
  const speed = speedKey(points);
  const ias = speed ? beforeAfter(rows, (point) => point?.[speed]) : { delta: null };
  const altitude = beforeAfter(rows, (point) => point?.altitude);
  const verticalSpeedAvgFpm = averageValues(rows.map((point) => point?.verticalSpeed).filter(Number.isFinite));
  const candidate = {
    startTime: rows[0].elapsedSeconds,
    endTime: rows.at(-1).elapsedSeconds,
    centerTime: (rows[0].elapsedSeconds + rows.at(-1).elapsedSeconds) / 2,
    phase: dominantPhase(rows),
    sampleCount: rows.length,
    fuelFlowBefore: fuelFlow.before,
    fuelFlowAfter: fuelFlow.after,
    fuelFlowDeltaGph: fuelFlow.delta,
    rpmBefore: rpm.before,
    rpmAfter: rpm.after,
    rpmDelta: rpm.delta,
    mapBefore: map.before,
    mapAfter: map.after,
    mapDeltaInHg: map.delta,
    percentPowerBefore: percentPower.before,
    percentPowerAfter: percentPower.after,
    percentPowerDelta: percentPower.delta,
    avgEgtBefore: avgEgt.before,
    avgEgtAfter: avgEgt.after,
    avgEgtDeltaF: avgEgt.delta,
    avgChtBefore: avgCht.before,
    avgChtAfter: avgCht.after,
    avgChtDeltaF: avgCht.delta,
    presentEgtCount: egtDeltas.length,
    iasDeltaKt: ias.delta,
    altitudeDeltaFt: altitude.delta,
    verticalSpeedAvgFpm,
    ...coordination,
  };
  if (triggerScore(candidate) < 1) return null;
  const { tags, interpretation } = classifyCandidate(candidate, missingChannels);
  return {
    ...candidate,
    contextTags: tags,
    interpretation,
    severity: ["single-cylinder-event", "not-power-change-explained"].includes(interpretation) ? "watch" : "info",
    channelKey: interpretation === "single-cylinder-event" && Number.isFinite(candidate.dominantCylinder) ? `egt${candidate.dominantCylinder}` : null,
    sourceCylinder: interpretation === "single-cylinder-event" ? candidate.dominantCylinder : null,
    rank: triggerScore(candidate),
  };
}

const FIND_CANDIDATES_STRIDE = 15;

function findCandidates(points, egtKeys, chtKeys, missingChannels) {
  const candidates = [];
  const endIndicesByDuration = precomputeEndIndicesByDuration(points, powerChangeContextParameters.windowSeconds);
  for (let startIndex = 0; startIndex < points.length - 1; startIndex += FIND_CANDIDATES_STRIDE) {
    for (let durationIdx = 0; durationIdx < powerChangeContextParameters.windowSeconds.length; durationIdx += 1) {
      const endIndex = endIndicesByDuration[durationIdx][startIndex];
      if (endIndex < 0) continue;
      const window = points.slice(startIndex, endIndex + 1);
      const candidate = candidateForWindow(points, window, egtKeys, chtKeys, missingChannels);
      if (candidate) candidates.push(candidate);
    }
  }
  return candidates;
}

function candidateSortValue(candidate) {
  const severity = {
    "single-cylinder-event": 80,
    "not-power-change-explained": 70,
    "insufficient-context": 50,
  }[candidate.interpretation] ?? 20;
  return severity + candidate.rank * 10;
}

function clusteredCandidates(candidates) {
  const sorted = candidates.slice().sort((a, b) => candidateSortValue(b) - candidateSortValue(a));
  const selected = [];
  for (const candidate of sorted) {
    const overlaps = selected.some((item) => candidate.startTime <= item.endTime && candidate.endTime >= item.startTime);
    if (!overlaps) selected.push(candidate);
    if (selected.length >= powerChangeContextParameters.maxEvents) break;
  }
  return selected.sort((a, b) => a.startTime - b.startTime);
}

function labelForEvent(event) {
  const labels = {
    "power-addition-context": "Power addition context",
    "power-reduction-context": "Power reduction context",
    "mixture-enrichment-context": "Mixture enrichment context",
    "mixture-leaning-context": "Mixture leaning context",
    "low-power-landing-context": "Low-power landing context",
    "shutdown-context": "Shutdown power-change context",
    "single-cylinder-event": "Single-cylinder EGT event",
    "insufficient-context": "Power-change context limited",
    "not-power-change-explained": "Unexplained engine response",
  };
  return labels[event.interpretation] ?? "Power change context";
}

function hintForEvent(event) {
  if (event.interpretation === "mixture-enrichment-context") return "All-cylinder EGT drop coincided with fuel-flow increase, consistent with mixture enrichment context.";
  if (event.interpretation === "mixture-leaning-context") return "All-cylinder EGT rise coincided with fuel-flow decrease, consistent with mixture leaning context.";
  if (event.interpretation === "low-power-landing-context") return "EGT and power channels dropped together during landing/rollout, consistent with low-power landing context.";
  if (event.interpretation === "shutdown-context") return "Power channels dropped during shutdown, so nearby engine-monitor movement should be treated as shutdown context.";
  if (event.interpretation === "power-addition-context") return "Power channels increased together, consistent with an intentional power addition.";
  if (event.interpretation === "power-reduction-context") return "Power channels decreased together, consistent with an intentional power reduction.";
  if (event.interpretation === "single-cylinder-event") return "EGT movement was concentrated on one cylinder rather than coordinated across the engine.";
  if (event.interpretation === "insufficient-context") return "EGT movement was coordinated, but one or more power-context channels were missing.";
  return "Engine-monitor movement was not well explained by fuel flow, RPM, MAP, phase, or air-data context.";
}

function eventFromCandidate(candidate) {
  const keyInterpretation = candidate.interpretation.replace(/-context$/, "").replace(/[^a-z0-9]+/g, "-");
  return {
    key: `power-change-${keyInterpretation}-${Math.round(candidate.startTime)}-${Math.round(candidate.endTime)}`,
    label: labelForEvent(candidate),
    severity: candidate.severity,
    startTime: round(candidate.startTime),
    endTime: round(candidate.endTime),
    centerTime: round(candidate.centerTime),
    phase: candidate.phase,
    interpretation: candidate.interpretation,
    contextTags: candidate.contextTags,
    sourceCylinder: Number.isFinite(candidate.sourceCylinder) ? candidate.sourceCylinder : null,
    channelKey: candidate.channelKey,
    stats: {
      fuelFlowDeltaGph: round(candidate.fuelFlowDeltaGph),
      rpmDelta: round(candidate.rpmDelta),
      mapDeltaInHg: round(candidate.mapDeltaInHg),
      percentPowerBefore: round(candidate.percentPowerBefore),
      percentPowerAfter: round(candidate.percentPowerAfter),
      percentPowerDelta: round(candidate.percentPowerDelta),
      avgEgtDeltaF: round(candidate.avgEgtDeltaF),
      avgChtDeltaF: round(candidate.avgChtDeltaF),
      sameDirectionEgtCount: candidate.sameDirectionEgtCount,
      presentEgtCount: candidate.presentEgtCount,
      sameDirectionEgtRatio: round(candidate.sameDirectionEgtRatio, 2),
      iasDeltaKt: round(candidate.iasDeltaKt),
      altitudeDeltaFt: round(candidate.altitudeDeltaFt),
      verticalSpeedAvgFpm: round(candidate.verticalSpeedAvgFpm),
    },
    interpretationHint: hintForEvent(candidate),
  };
}

function primaryInterpretation(events) {
  if (!events.length) return null;
  const counts = new Map();
  events.forEach((event) => counts.set(event.interpretation, (counts.get(event.interpretation) ?? 0) + 1));
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
}

function scoreForEvents(events) {
  if (!events.length) return 100;
  const unresolved = events.filter((event) => event.interpretation === "not-power-change-explained").length;
  const singleCylinder = events.filter((event) => event.interpretation === "single-cylinder-event").length;
  const insufficient = events.filter((event) => event.interpretation === "insufficient-context").length;
  if (unresolved || singleCylinder) return clampScore(86 - unresolved * 8 - singleCylinder * 4);
  if (insufficient) return clampScore(94 - insufficient * 2);
  return 98;
}

function noteForEvents(events, score) {
  if (!events.length) return "No major power-change transitions detected";
  const singleCylinder = events.filter((event) => event.interpretation === "single-cylinder-event").length;
  if (singleCylinder) return `${singleCylinder} EGT event(s) were cylinder-specific`;
  const unresolved = events.filter((event) => event.interpretation === "not-power-change-explained").length;
  if (unresolved) return `${unresolved} engine-response event(s) need more context`;
  if (score < 98) return "Power-change context is incomplete but useful";
  return "Power/mixture transitions are context-explained";
}

function statusForScore(score) {
  if (!Number.isFinite(score)) return null;
  if (score >= 88) return "Good";
  if (score >= 72) return "Watch";
  return "Review";
}

export function analyzePowerChangeContext(data = []) {
  const points = sortedTimedPoints(Array.isArray(data) ? data : []);
  const egtKeys = presentKeys(points, "egt");
  const chtKeys = presentKeys(points, "cht");
  const missingChannels = missingContextChannels(points);
  if (!points.length || !egtKeys.length) {
    return {
      key: "powerChangeContext",
      score: null,
      note: "Need EGT samples with elapsed time",
      details: {
        events: [],
        missingContextChannels: missingChannels,
        eventCount: 0,
        explainedEventCount: 0,
        unexplainedEventCount: 0,
        allCylinderEventCount: 0,
        primaryInterpretation: null,
        largestFuelFlowDeltaGph: null,
        largestPercentPowerDelta: null,
        largestAvgEgtDeltaF: null,
      },
    };
  }

  const events = clusteredCandidates(findCandidates(points, egtKeys, chtKeys, missingChannels)).map(eventFromCandidate);
  const score = scoreForEvents(events);
  const explainedEventCount = events.filter((event) => !["not-power-change-explained", "single-cylinder-event", "insufficient-context"].includes(event.interpretation)).length;
  const unexplainedEventCount = events.filter((event) => ["not-power-change-explained", "single-cylinder-event"].includes(event.interpretation)).length;
  return {
    key: "powerChangeContext",
    score,
    note: noteForEvents(events, score),
    details: {
      events,
      missingContextChannels: missingChannels,
      eventCount: events.length,
      explainedEventCount,
      unexplainedEventCount,
      allCylinderEventCount: events.filter((event) => event.contextTags.includes("all-cylinder-egt-response")).length,
      primaryInterpretation: primaryInterpretation(events),
      largestFuelFlowDeltaGph: round(Math.max(...events.map((event) => Math.abs(event.stats.fuelFlowDeltaGph ?? 0)), 0)),
      largestPercentPowerDelta: round(Math.max(...events.map((event) => Math.abs(event.stats.percentPowerDelta ?? 0)), 0)),
      largestAvgEgtDeltaF: round(Math.max(...events.map((event) => Math.abs(event.stats.avgEgtDeltaF ?? 0)), 0)),
    },
  };
}
